import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown, ChevronRight, Home, BookOpen, Globe, MapPin, Briefcase, TrendingUp, Calendar, Bell, Award, Zap, Users, Building, ExternalLink } from 'lucide-react';

const CurrentAffairsPortal = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('national');
  const [selectedExam, setSelectedExam] = useState('upsc');
  const [expandedSections, setExpandedSections] = useState({ 'Central Government': true });
  const [news, setNews] = useState([]);
  const [liveUpdates, setLiveUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedNews, setSelectedNews] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Exam categories with icons
  const examCategories = {
    'Central Government': {
      icon: <Globe size={18} />,
      exams: [
        { id: 'upsc', name: 'UPSC Civil Services', icon: <Award size={16} /> },
        { id: 'ssc-cgl', name: 'SSC CGL', icon: <Users size={16} /> },
        { id: 'ssc-chsl', name: 'SSC CHSL', icon: <Users size={16} /> },
        { id: 'railway', name: 'Railway Exams', icon: <Building size={16} /> },
        { id: 'banking', name: 'Banking (IBPS/SBI)', icon: <Briefcase size={16} /> },
        { id: 'insurance', name: 'Insurance', icon: <Briefcase size={16} /> },
        { id: 'nda', name: 'NDA/CDS', icon: <Award size={16} /> },
      ]
    },
    'PSU & Corporations': {
      icon: <Briefcase size={18} />,
      exams: [
        { id: 'ongc', name: 'ONGC', icon: <Building size={16} /> },
        { id: 'ntpc', name: 'NTPC', icon: <Zap size={16} /> },
        { id: 'barc', name: 'BARC', icon: <BookOpen size={16} /> },
        { id: 'gail', name: 'GAIL', icon: <Building size={16} /> },
        { id: 'iocl', name: 'IOCL', icon: <Building size={16} /> },
      ]
    },
    'State Government': {
      icon: <MapPin size={18} />,
      exams: [
        { id: 'state-pcs', name: 'State PCS', icon: <Award size={16} /> },
        { id: 'state-police', name: 'State Police', icon: <Users size={16} /> },
        { id: 'state-teaching', name: 'Teaching Exams', icon: <BookOpen size={16} /> },
      ]
    },
    'Teaching': {
      icon: <BookOpen size={18} />,
      exams: [
        { id: 'ugc-net', name: 'UGC NET', icon: <BookOpen size={16} /> },
        { id: 'kvs', name: 'KVS', icon: <Users size={16} /> },
        { id: 'ctet', name: 'CTET', icon: <BookOpen size={16} /> },
      ]
    }
  };

  const categories = [
    { id: 'national', name: 'National', icon: <MapPin size={18} />, query: 'India news' },
    { id: 'international', name: 'International', icon: <Globe size={18} />, query: 'world news' },
    { id: 'economy', name: 'Economy', icon: <TrendingUp size={18} />, query: 'economy business' },
    { id: 'science', name: 'Science & Tech', icon: <BookOpen size={18} />, query: 'science technology' },
    { id: 'sports', name: 'Sports', icon: <TrendingUp size={18} />, query: 'sports' },
    { id: 'awards', name: 'Awards', icon: <Award size={18} />, query: 'awards honors' }
  ];

  useEffect(() => {
    fetchNews();
    fetchLiveUpdates();
  }, [selectedCategory, selectedExam]);

  const fetchNews = async () => {
    setLoading(true);
    try {
      // Method 1: Using NewsAPI.org (Register at https://newsapi.org for FREE API key)
      const NEWS_API_KEY = 'YOUR_NEWSAPI_KEY'; // Get from newsapi.org
      
      const categoryQuery = categories.find(c => c.id === selectedCategory)?.query || 'india';
      
      const newsApiResponse = await fetch(
        `https://newsapi.org/v2/everything?q=${categoryQuery}&language=en&sortBy=publishedAt&pageSize=20&apiKey=${NEWS_API_KEY}`
      );

      if (newsApiResponse.ok) {
        const newsData = await newsApiResponse.json();
        if (newsData.articles && newsData.articles.length > 0) {
          const formattedNews = newsData.articles.map((article, index) => ({
            id: article.url,
            title: article.title,
            description: article.description || 'Click to read more about this news',
            content: article.content || article.description,
            category: selectedCategory,
            date: new Date(article.publishedAt).toLocaleDateString('en-IN'),
            source: article.source.name,
            importance: index < 5 ? 'high' : 'medium',
            url: article.url,
            imageUrl: article.urlToImage,
            author: article.author,
            publishedAt: article.publishedAt
          }));
          setNews(formattedNews);
          setLoading(false);
          return;
        }
      }

      // Method 2: Fallback to The Guardian API (FREE - get key from https://open-platform.theguardian.com)
      const GUARDIAN_API_KEY = 'test'; // Replace with your Guardian API key
      const guardianResponse = await fetch(
        `https://content.guardianapis.com/search?q=${categoryQuery}&page-size=20&show-fields=all&api-key=${GUARDIAN_API_KEY}`
      );
      
      if (guardianResponse.ok) {
        const guardianData = await guardianResponse.json();
        const formattedNews = guardianData.response.results.map((item, index) => ({
          id: item.id,
          title: item.webTitle,
          description: item.fields?.trailText || 'Read more about this important development',
          content: item.fields?.bodyText || item.fields?.trailText,
          category: selectedCategory,
          date: new Date(item.webPublicationDate).toLocaleDateString('en-IN'),
          source: item.sectionName,
          importance: index < 5 ? 'high' : 'medium',
          url: item.webUrl,
          imageUrl: item.fields?.thumbnail,
          author: item.fields?.byline,
          publishedAt: item.webPublicationDate
        }));
        setNews(formattedNews);
      } else {
        setNews(getStaticNews());
      }
    } catch (error) {
      console.error('Error fetching news:', error);
      setNews(getStaticNews());
    }
    setLoading(false);
  };

  const fetchLiveUpdates = async () => {
    try {
      // Using NewsAPI for live breaking news
      const NEWS_API_KEY = 'YOUR_NEWSAPI_KEY';
      const response = await fetch(
        `https://newsapi.org/v2/top-headlines?country=in&pageSize=10&apiKey=${NEWS_API_KEY}`
      );
      
      if (response.ok) {
        const data = await response.json();
        const updates = data.articles?.slice(0, 6).map((article, index) => ({
          id: article.url,
          title: article.title,
          time: getTimeAgo(article.publishedAt),
          type: index === 0 ? 'BREAKING' : index < 3 ? 'TRENDING' : 'UPDATE',
          url: article.url
        }));
        setLiveUpdates(updates || getStaticUpdates());
      } else {
        setLiveUpdates(getStaticUpdates());
      }
    } catch (error) {
      setLiveUpdates(getStaticUpdates());
    }
  };

  const handleReadMore = async (newsItem) => {
    setSelectedNews(newsItem);
    setShowModal(true);
    setModalLoading(true);

    try {
      // Fetch full article content
      if (newsItem.url) {
        // You can use a web scraping API or just show available content
        setModalLoading(false);
      }
    } catch (error) {
      console.error('Error loading full article:', error);
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedNews(null);
  };

  const getStaticNews = () => [
    {
      id: 1,
      title: 'India\'s GDP Growth Projection Revised to 7.2% for FY 2024-25',
      description: 'Economic Survey highlights robust growth driven by manufacturing and services sectors.',
      content: 'The Economic Survey 2024-25 has projected India\'s GDP growth at 7.2% for the current fiscal year. Key drivers include:\n\n• Manufacturing sector showing 8.5% growth\n• Services sector contributing 65% to GDP\n• Government infrastructure spending increased by 35%\n• Foreign Direct Investment up by 28%\n• Digital economy growing at 15% annually\n\nImportant Points for Exams:\n✓ GDP growth rate: 7.2%\n✓ Manufacturing growth: 8.5%\n✓ FDI increase: 28%\n✓ Infrastructure spending: Up 35%\n✓ Digital economy growth: 15%',
      category: 'economy',
      date: new Date().toLocaleDateString('en-IN'),
      source: 'Economic Survey',
      importance: 'high',
      imageUrl: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=500'
    },
    {
      id: 2,
      title: 'UN Climate Summit COP29: Historic Carbon Neutrality Commitments',
      description: 'Global leaders conclude conference with breakthrough agreements on climate action.',
      content: 'The COP29 Climate Summit concluded with historic commitments:\n\n• 150+ countries pledge carbon neutrality by 2050\n• $500 billion climate fund established\n• Renewable energy targets increased to 70% by 2040\n• Green hydrogen production subsidies announced\n• India commits to 500 GW renewable capacity\n\nKey Takeaways for Exams:\n✓ COP29 location and year\n✓ Carbon neutrality target: 2050\n✓ Climate fund: $500 billion\n✓ Renewable target: 70% by 2040\n✓ India\'s renewable capacity: 500 GW',
      category: 'international',
      date: new Date().toLocaleDateString('en-IN'),
      source: 'UN Reports',
      importance: 'high',
      imageUrl: 'https://images.unsplash.com/photo-1569163139394-de4798aa62b6?w=500'
    },
    {
      id: 3,
      title: 'National Education Policy 2020: New Implementation Guidelines',
      description: 'Ministry of Education releases comprehensive framework for NEP implementation.',
      content: 'Ministry of Education announces major NEP 2020 updates:\n\n• Multidisciplinary education in all universities\n• Multiple entry-exit system implemented\n• Credit transfer framework established\n• National Higher Education Qualifications Framework (NHEQF)\n• Academic Bank of Credits (ABC) operational\n\nImportant for Exams:\n✓ NEP announced: 2020\n✓ 5+3+3+4 curriculum structure\n✓ Multiple entry-exit system\n✓ NHEQF introduced\n✓ ABC for credit transfer',
      category: 'national',
      date: new Date().toLocaleDateString('en-IN'),
      source: 'Ministry of Education',
      importance: 'high',
      imageUrl: 'https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=500'
    },
    {
      id: 4,
      title: 'ISRO Gaganyaan Mission: Successful Test Flight Completed',
      description: 'Critical unmanned test brings human spaceflight program closer to reality.',
      content: 'ISRO achieves major milestone in Gaganyaan mission:\n\n• Unmanned test flight successful\n• Crew module safely recovered\n• Life support systems tested\n• Launch abort system validated\n• First manned flight expected 2025-26\n\nKey Points:\n✓ Gaganyaan = Indian human spaceflight\n✓ Launch vehicle: GSLV Mk III\n✓ Crew capacity: 3 astronauts\n✓ Mission duration: 7 days\n✓ Expected launch: 2025-26',
      category: 'science',
      date: new Date().toLocaleDateString('en-IN'),
      source: 'ISRO',
      importance: 'high',
      imageUrl: 'https://images.unsplash.com/photo-1516849841032-87cbac4d88f7?w=500'
    },
    {
      id: 5,
      title: 'Supreme Court Environmental Judgment Sets New Precedent',
      description: 'Landmark ruling strengthens environmental protection laws nationwide.',
      content: 'Supreme Court delivers historic environmental judgment:\n\n• Stricter penalties for pollution violations\n• Mandatory environmental impact assessments\n• Green clearances timebound (6 months)\n• Public consultation mandatory\n• State accountability framework established\n\nExam Important Points:\n✓ Environmental Protection Act: 1986\n✓ National Green Tribunal: 2010\n✓ EIA mandatory for projects\n✓ Green clearance timeline: 6 months\n✓ Public consultation required',
      category: 'national',
      date: new Date().toLocaleDateString('en-IN'),
      source: 'Supreme Court',
      importance: 'high',
      imageUrl: 'https://images.unsplash.com/photo-1473186578172-c141e6798cf4?w=500'
    },
    {
      id: 6,
      title: 'India-US Strategic Partnership Enhanced with Defense Agreement',
      description: 'Bilateral talks result in comprehensive cooperation across sectors.',
      content: 'India-US strategic partnership strengthened:\n\n• Defense technology sharing agreement\n• Joint military exercises expanded\n• Trade facilitation measures\n• Education exchange programs\n• Clean energy collaboration\n\nKey Points for Exams:\n✓ 2+2 Dialogue mechanism\n✓ QUAD member countries\n✓ Defense agreements: BECA, LEMOA, COMCASA\n✓ Bilateral trade: $190+ billion\n✓ Strategic Clean Energy Partnership',
      category: 'international',
      date: new Date().toLocaleDateString('en-IN'),
      source: 'Ministry of External Affairs',
      importance: 'medium',
      imageUrl: 'https://images.unsplash.com/photo-1529107386315-e1a2ed48a620?w=500'
    },
    {
      id: 7,
      title: 'RBI Monetary Policy: Key Interest Rate Decisions Announced',
      description: 'Central bank maintains stance while focusing on inflation management.',
      content: 'RBI Monetary Policy Committee decisions:\n\n• Repo rate maintained at 6.5%\n• Reverse repo rate: 3.35%\n• Cash Reserve Ratio (CRR): 4.5%\n• Statutory Liquidity Ratio (SLR): 18%\n• GDP growth forecast: 7.2%\n• Inflation target: 4% (+/- 2%)\n\nImportant for Banking Exams:\n✓ Current repo rate: 6.5%\n✓ CRR: 4.5%\n✓ SLR: 18%\n✓ Inflation targeting: 4%\n✓ RBI Governor: Shaktikanta Das',
      category: 'economy',
      date: new Date().toLocaleDateString('en-IN'),
      source: 'Reserve Bank of India',
      importance: 'high',
      imageUrl: 'https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?w=500'
    },
    {
      id: 8,
      title: 'Asian Games 2024: India Wins Historic Gold Medals',
      description: 'Outstanding performance across multiple sporting categories.',
      content: 'India\'s medal tally at Asian Games:\n\n• Total medals: 100+ (approx)\n• Gold medals: 28\n• Athletics: 8 golds\n• Shooting: 6 golds\n• Wrestling: 5 golds\n• Best performance in history\n\nSports Current Affairs:\n✓ Asian Games held in: China\n✓ India\'s rank: Top 5\n✓ Medal tally: 100+\n✓ Gold medals: 28\n✓ Star performers: Neeraj Chopra, PV Sindhu',
      category: 'sports',
      date: new Date().toLocaleDateString('en-IN'),
      source: 'Sports Authority of India',
      importance: 'medium',
      imageUrl: 'https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=500'
    }
  ];

  const getStaticUpdates = () => {
    const updates = [];
    const types = ['BREAKING', 'TRENDING', 'TRENDING', 'UPDATE', 'UPDATE', 'UPDATE'];
    const baseUpdates = [
      'Parliament Session: Major bills passed today',
      'PM announces new farmer welfare scheme',
      'India ranks 40th in Global Innovation Index',
      'National team qualifies for World Cup finals',
      'Digital India reaches 1 billion milestone',
      'New metro line inaugurated in Delhi',
      'Supreme Court delivers landmark judgment',
      'RBI announces monetary policy review',
      'New education scheme launched nationwide',
      'India signs trade agreement with EU',
      'ISRO announces next satellite launch',
      'Finance Minister presents economic report',
      'New infrastructure project worth ₹5000 crore',
      'Health Ministry announces vaccination drive',
      'Indian athletes win medals at international event',
      'Government announces relief package for farmers',
      'Tech summit concludes with major announcements',
      'New policy for renewable energy sector',
      'Border security enhanced with new technology',
      'Education reforms get cabinet approval',
      'Defence Ministry signs new aircraft deal',
      'Smart city project launched in 10 cities',
      'Agriculture sector gets ₹1 lakh crore boost',
      'New cyber security policy announced',
      'Space mission scheduled for next month',
      'Railway announces 100 new trains',
      'Digital payment crosses ₹10 trillion mark',
      'New labour codes implemented',
      'Healthcare budget increased by 30%',
      'Green energy target revised upward',
      'Startup India completes 5 years',
      'New semiconductor policy announced',
      'Electric vehicle subsidy extended',
      'Financial inclusion reaches 95% households',
      'Trade deficit narrows in Q3',
      'Manufacturing PMI hits 5-year high',
      'FDI inflows increase by 25%',
      'GST collection reaches record high',
      'Infrastructure spending doubled',
      'Public sector banks show profit growth',
      'Stock market hits all-time high',
      'Inflation rate drops to 4.5%',
      'Employment rate shows improvement',
      'Export growth exceeds targets',
      'Foreign reserves at comfortable level',
      'Digital literacy program expanded',
      'Skill development initiative launched',
      'Women entrepreneurship fund announced',
      'Rural connectivity project completed',
      'Clean energy capacity reaches 200 GW'
    ];
    
    baseUpdates.forEach((title, index) => {
      updates.push({
        id: index + 1,
        title: title,
        time: `${index < 60 ? index + 1 : Math.floor((index + 1) / 60)} ${index < 60 ? 'min' : 'hour'}${index > 0 ? 's' : ''} ago`,
        type: types[index % types.length]
      });
    });
    
    return updates;
  };

  const getTimeAgo = (date) => {
    const minutes = Math.floor((new Date() - new Date(date)) / 60000);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    return `${Math.floor(hours / 24)} day${Math.floor(hours / 24) > 1 ? 's' : ''} ago`;
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleExamSelect = (examId) => {
    setSelectedExam(examId);
  };

  const NewsCard = ({ item }) => (
    <div className="card mb-3 shadow-sm border-0 h-100">
      {item.imageUrl && (
        <img src={item.imageUrl} className="card-img-top" alt={item.title} style={{ height: '150px', objectFit: 'cover' }} />
      )}
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <span className={`badge ${item.importance === 'high' ? 'bg-danger' : 'bg-warning text-dark'}`}>
            {item.importance === 'high' ? '⭐ Must Know' : '📌 Important'}
          </span>
          <small className="text-muted">{item.date}</small>
        </div>
        <h5 className="card-title mb-2">{item.title}</h5>
        <p className="card-text text-muted small">{item.description}</p>
        <div className="d-flex justify-content-between align-items-center mt-3">
          <small className="text-muted">📰 {item.source}</small>
          <button 
            className="btn btn-sm btn-primary"
            onClick={() => handleReadMore(item)}
          >
            Read More →
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="ca-portal-wrapper my-5 py-5 vh-100">
      {/* Internal Navbar */}
      <div className="ca-navbar" style={{ background: '#58A95B', padding: '1.5rem' }}>
        <div className="ca-navbar-content">
          <div className="d-flex align-items-center">
            <button 
              className="btn btn-link text-white p-0 me-3"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <Menu size={24} />
            </button>
            <span className="navbar-brand mb-0 h1 fw-bold d-flex align-items-center">
              <BookOpen size={24} className="me-2" />
              <span className="d-none d-md-inline">Current Affairs Portal</span>
              <span className="d-md-none">CA Portal</span>
            </span>
          </div>
          <div className="d-flex align-items-center gap-3">
            <span className="badge bg-light text-primary d-none d-sm-flex align-items-center">
              <Calendar size={14} className="me-1" />
              {new Date().toLocaleDateString('en-IN')}
            </span>
            <Bell size={20} className="text-white" />
          </div>
        </div>
      </div>

      {/* Three Column Layout */}
      <div className="ca-main-container">
        {/* Left Sidebar */}
        <div 
          className="ca-sidebar-left"
          style={{
            width: sidebarOpen ? '280px' : '60px'
          }}
        >
          <div className="ca-sidebar-scroll">
            {sidebarOpen ? (
              <>
                <h6 className="text-muted mb-3 text-uppercase small px-2">Exam Categories</h6>
                {Object.entries(examCategories).map(([category, data]) => (
                  <div key={category} className="mb-2">
                    <button
                      className="btn btn-light w-100 text-start d-flex justify-content-between align-items-center p-2"
                      onClick={() => toggleSection(category)}
                    >
                      <span className="d-flex align-items-center gap-2 small">
                        {data.icon}
                        <span className="fw-semibold">{category}</span>
                      </span>
                      {expandedSections[category] ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </button>
                    
                    {expandedSections[category] && (
                      <div className="ms-2 mt-1">
                        {data.exams.map(exam => (
                          <button
                            key={exam.id}
                            className={`btn btn-sm w-100 text-start mb-1 d-flex align-items-center gap-2 ${
                              selectedExam === exam.id ? 'btn-primary' : 'btn-outline-secondary'
                            }`}
                            onClick={() => handleExamSelect(exam.id)}
                            style={{ fontSize: '0.85rem', padding: '0.4rem 0.6rem' }}
                          >
                            {exam.icon}
                            <span>{exam.name}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </>
            ) : (
              <div className="d-flex flex-column align-items-center gap-3 py-2">
                {Object.entries(examCategories).map(([category, data]) => (
                  <button
                    key={category}
                    className="btn btn-light p-2"
                    onClick={() => {
                      setSidebarOpen(true);
                      toggleSection(category);
                    }}
                    title={category}
                  >
                    {data.icon}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Middle Content */}
        <div className="ca-content-middle">
          <div className="ca-content-scroll">
            <div className="mb-4">
              <h4 className="mb-3">Daily Current Affairs - <span className="text-primary">{selectedExam.toUpperCase()}</span></h4>
              <div className="d-flex flex-wrap gap-2">
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    className={`btn btn-sm ${selectedCategory === cat.id ? 'btn-primary' : 'btn-outline-primary'} d-flex align-items-center gap-2`}
                    onClick={() => setSelectedCategory(cat.id)}
                  >
                    {cat.icon}
                    <span className="d-none d-sm-inline">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="mt-3 text-muted">Loading latest updates...</p>
              </div>
            ) : (
              <div className="row row-cols-1 row-cols-lg-2 g-3">
                {news.filter(item => item.category === selectedCategory).map(item => (
                  <div key={item.id} className="col">
                    <NewsCard item={item} />
                  </div>
                ))}
              </div>
            )}

            <div className="card mt-4 bg-info bg-opacity-10 border-info">
              <div className="card-body">
                <h6 className="card-title">🚀 API Integration Guide</h6>
                <p className="small mb-2"><strong>Free News APIs:</strong></p>
                <ul className="small mb-0">
                  <li><strong>NewsAPI.org:</strong> Register at newsapi.org (100 requests/day free)</li>
                  <li><strong>The Guardian:</strong> open-platform.theguardian.com (Unlimited, free)</li>
                  <li><strong>GNews API:</strong> gnews.io (100 requests/day free)</li>
                  <li><strong>Mediastack:</strong> mediastack.com (500 requests/month free)</li>
                </ul>
                <p className="small mt-2 mb-0"><em>Replace API keys in code to get live news data!</em></p>
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="ca-sidebar-right d-none d-lg-flex" style={{ flexDirection: 'column', minHeight: 0 }}>
          <div style={{ 
            overflowY: 'auto', 
            overflowX: 'hidden', 
            flex: '1 1 auto',
            padding: '1rem',
            height: '100%'
          }}>
            <h6 className="text-danger mb-3 d-flex align-items-center gap-2">
              <div className="spinner-grow spinner-grow-sm text-danger" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              LIVE UPDATES
            </h6>
            
            {liveUpdates.map(update => (
              <div key={update.id} className="card border-0 shadow-sm mb-2">
                <div className="card-body p-2">
                  <span className={`badge ${
                    update.type === 'BREAKING' ? 'bg-danger' : 
                    update.type === 'TRENDING' ? 'bg-warning text-dark' : 'bg-info'
                  } mb-1`} style={{ fontSize: '0.65rem' }}>
                    {update.type}
                  </span>
                  <p className="mb-1 small fw-semibold">{update.title}</p>
                  <small className="text-muted" style={{ fontSize: '0.75rem' }}>🕐 {update.time}</small>
                </div>
              </div>
            ))}

            <h6 className="text-muted mb-3 mt-4">📅 UPCOMING EXAMS</h6>
            <div className="card border-0 shadow-sm mb-2">
              <div className="card-body p-2">
                <p className="mb-1 small fw-bold">UPSC Prelims 2025</p>
                <small className="text-muted">Expected: May 2025</small>
              </div>
            </div>
            <div className="card border-0 shadow-sm mb-2">
              <div className="card-body p-2">
                <p className="mb-1 small fw-bold">SSC CGL 2025</p>
                <small className="text-muted">Expected: June 2025</small>
              </div>
            </div>

            <h6 className="text-muted mb-3 mt-4">🎯 QUICK FACTS</h6>
            <div className="card border-0 shadow-sm">
              <div className="card-body p-2" style={{ fontSize: '0.85rem' }}>
                <p className="mb-1"><strong>President:</strong> Droupadi Murmu</p>
                <p className="mb-1"><strong>PM:</strong> Narendra Modi</p>
                <p className="mb-1"><strong>Vice President:</strong> Jagdeep Dhankhar</p>
                <p className="mb-0"><strong>GDP Growth:</strong> ~7.2%</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Popup for Full Article */}
      {showModal && (
        <div className="ca-modal-overlay" onClick={closeModal}>
          <div className="ca-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="ca-modal-header">
              <h5 className="mb-0">{selectedNews?.title}</h5>
              <button className="btn btn-link text-dark p-0" onClick={closeModal}>
                <X size={24} />
              </button>
            </div>
            <div className="ca-modal-body">
              {modalLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              ) : (
                <>
                  {selectedNews?.imageUrl && (
                    <img 
                      src={selectedNews.imageUrl} 
                      alt={selectedNews.title}
                      className="img-fluid mb-3 rounded"
                      style={{ maxHeight: '300px', width: '100%', objectFit: 'cover' }}
                    />
                  )}
                  
                  <div className="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                    <div>
                      <span className="badge bg-primary me-2">{selectedNews?.source}</span>
                      <span className="badge bg-secondary">{selectedNews?.category}</span>
                    </div>
                    <small className="text-muted">{selectedNews?.date}</small>
                  </div>

                  {selectedNews?.author && (
                    <p className="text-muted small mb-3">
                      <strong>By:</strong> {selectedNews.author}
                    </p>
                  )}

                  <div className="article-content" style={{ whiteSpace: 'pre-line', lineHeight: '1.8' }}>
                    {selectedNews?.content || selectedNews?.description}
                  </div>

                  {selectedNews?.url && (
                    <div className="mt-4 pt-3 border-top">
                      <a 
                        href={selectedNews.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="btn btn-outline-primary btn-sm d-flex align-items-center justify-content-center gap-2"
                      >
                        Read Full Article <ExternalLink size={16} />
                      </a>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .ca-portal-wrapper {
          width: 100%;
          height: 600px;
          display: flex;
          flex-direction: column;
          background: #fff;
          position: relative;
          overflow: hidden;
        }
        
        .ca-navbar {
          background: #58A95B;
          color: white;
          padding: 1.5rem;
          flex-shrink: 0;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
        
        .ca-navbar-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }
        
        .ca-main-container {
          display: flex;
          flex: 1;
          overflow: hidden;
          min-height: 0;
        }
        
        .ca-sidebar-left {
          background: #f8f9fa;
          border-right: 1px solid #dee2e6;
          flex-shrink: 0;
          transition: width 0.3s ease;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        
        .ca-sidebar-scroll {
          overflow-y: auto;
          overflow-x: hidden;
          flex: 1;
          padding: 1rem;
        }
        
        .ca-content-middle {
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        
        .ca-content-scroll {
          overflow-y: auto;
          overflow-x: hidden;
          flex: 1;
          padding: 1.5rem;
        }
        
        .ca-sidebar-right {
          width: 280px;
          background: #f8f9fa;
          border-left: 1px solid #dee2e6;
          flex-shrink: 0;
          display: flex;
          flex-direction: column;
          min-height: 0;
        }

        /* Modal Styles */
        .ca-modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          padding: 20px;
        }

        .ca-modal-content {
          background: white;
          border-radius: 12px;
          max-width: 900px;
          width: 100%;
          max-height: 90vh;
          display: flex;
          flex-direction: column;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
          overflow: hidden;
        }

        .ca-modal-header {
          padding: 1.5rem;
          border-bottom: 1px solid #dee2e6;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-shrink: 0;
        }

        .ca-modal-body {
          padding: 1.5rem;
          overflow-y: auto;
          flex: 1;
        }

        .ca-modal-body::-webkit-scrollbar {
          width: 8px;
        }

        .ca-modal-body::-webkit-scrollbar-track {
          background: #f1f1f1;
        }

        .ca-modal-body::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }

        .ca-modal-body::-webkit-scrollbar-thumb:hover {
          background: #555;
        }

        .article-content {
          font-size: 1rem;
          color: #333;
        }
        
        .card {
          transition: all 0.2s ease;
        }
        
        .card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
        }
        
        .btn {
          transition: all 0.2s ease;
        }
        
        .ca-sidebar-scroll::-webkit-scrollbar,
        .ca-content-scroll::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        
        .ca-sidebar-scroll::-webkit-scrollbar-track,
        .ca-content-scroll::-webkit-scrollbar-track {
          background: #f1f1f1;
        }
        
        .ca-sidebar-scroll::-webkit-scrollbar-thumb,
        .ca-content-scroll::-webkit-scrollbar-thumb {
          background: #888;
          border-radius: 4px;
        }
        
        .ca-sidebar-scroll::-webkit-scrollbar-thumb:hover,
        .ca-content-scroll::-webkit-scrollbar-thumb:hover {
          background: #555;
        }

        @media (max-width: 991px) {
          .ca-sidebar-right {
            display: none !important;
          }
        }
        
        @media (max-width: 768px) {
          .ca-portal-wrapper {
            height: 100vh;
          }
          
          .ca-sidebar-left {
            position: absolute;
            left: 0;
            top: 0;
            bottom: 0;
            z-index: 1000;
            box-shadow: 2px 0 8px rgba(0,0,0,0.1);
          }

          .ca-modal-content {
            max-height: 95vh;
            margin: 10px;
          }

          .ca-modal-body {
            padding: 1rem;
          }
        }
      `}</style>
    </div>
  );
};

export default CurrentAffairsPortal;